import React, { useCallback, useMemo } from 'react';
import { SelectRenderer } from 'react-dropdown-select';
import { Field, FieldArray, FieldArrayRenderProps, FieldProps } from 'formik';
import Fuse from 'fuse.js';
import isEqual from 'lodash/isEqual';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import { FieldWrapper, TimeMaskedInput } from 'components';
import { ISelectOption } from 'components/Select/types';
import { IJobTitleState } from 'store/types';
import { ILaborSchedule } from 'types';
import { AddBreakButton, AddShiftButton, BreakEndCell, BreakRow, BreaksSection, EditShiftHeader, EditShiftLayout, RemoveButton, ShiftFieldsRow, ShiftHeading, ShiftJobTitleSelect, TimeField } from './styles';
import { findBreaksOutsideShift, findOverlappingBreaks, isTimeOutsideShift, REASON_MAX_LENGTH, REASON_TOO_LONG_MESSAGE, TIME_PATTERN, toIsoInstant, toIsoInstantAfter, toLocalTime } from './utils';

// Fuzzy match on label only: job titles are short, so a strict/prefix search misses obvious typos.
const searchJobTitleOptions = (options: ISelectOption<IJobTitleState>[], query: string) => new Fuse(options, { keys: ['label'], threshold: 0.2, ignoreLocation: true }).search(query).map((result) => result.item);

const toJobTitleOption = (title: string): ISelectOption<IJobTitleState> => ({ label: title, value: { title } });

export interface IShiftFormBreak {
  id: string;
  start: string;
  end: string;
}

export interface IShiftFormSchedule {
  id: string;
  jobTitle: string;
  start: string;
  end: string;
  breaks: IShiftFormBreak[];
}

export interface IShiftFormValues {
  schedules: IShiftFormSchedule[];
  reason: string;
}

export const toFormSchedule = (schedule: ILaborSchedule, timezone: string): IShiftFormSchedule => ({
  id: uuid(),
  jobTitle: schedule.jobTitle,
  start: toLocalTime({ isoInstant: schedule.start, timezone }),
  end: schedule.end ? toLocalTime({ isoInstant: schedule.end, timezone }) : '',
  breaks: schedule.breaks.map((laborBreak) => ({
    id: uuid(),
    start: toLocalTime({ isoInstant: laborBreak.start, timezone }),
    end: laborBreak.end ? toLocalTime({ isoInstant: laborBreak.end, timezone }) : '',
  })),
});

export const newFormSchedule = (): IShiftFormSchedule => ({ id: uuid(), jobTitle: '', start: '', end: '', breaks: [] });

const withoutIds = (schedules: IShiftFormSchedule[]) => schedules.map(({ jobTitle, start, end, breaks }) => ({ jobTitle, start, end, breaks: breaks.map((laborBreak) => ({ start: laborBreak.start, end: laborBreak.end })) }));

export const haveSchedulesChanged = (initial: IShiftFormSchedule[], current: IShiftFormSchedule[]) => !isEqual(withoutIds(initial), withoutIds(current));

interface IToLaborScheduleParams {
  schedule: IShiftFormSchedule;
  date: string;
  timezone: string;
  businessDayStart: string;
}

/**
 * Inverse of `toFormSchedule`. Only the clock-in is placed by the business day rule; every other time is
 * the first occurrence after the time it follows (clock-out and break starts after the clock-in, break
 * ends after their start), so a shift that runs past the next business day start (e.g. 04:00 -> 20:00
 * when the day starts at 06:00) keeps its later times on the right day.
 */
export const toLaborSchedule = ({ schedule, date, timezone, businessDayStart }: IToLaborScheduleParams): ILaborSchedule => {
  const start = toIsoInstant({ date, time: schedule.start, timezone, businessDayStart });

  return {
    jobTitle: schedule.jobTitle,
    start,
    end: toIsoInstantAfter({ time: schedule.end, timezone, after: start }),
    breaks: schedule.breaks.map((laborBreak) => {
      const breakStart = toIsoInstantAfter({ time: laborBreak.start, timezone, after: start });
      return { start: breakStart, end: toIsoInstantAfter({ time: laborBreak.end, timezone, after: breakStart }) };
    }),
  };
};

// Built fresh (rather than reusing lib/Validations' shared timeValidationSchema) because re-calling
// `.required(message)` on an already-built schema re-orders its internal test list, which changes
// which message ("Required field" vs "Invalid time") wins on an empty value.
const timeField = (requiredMessage: string) => Yup.string().required(requiredMessage).matches(TIME_PATTERN, 'Invalid time');

interface IRejectZeroLengthParams {
  context: Yup.TestContext;
  range: { start: string; end: string };
  message: string;
}

// A range whose end reads the same as its start is ambiguous: `toLaborSchedule` would save it as zero minutes,
// while the checks here would read it as a full day. Neither is a real shift or break, so reject it on the end time.
const rejectZeroLength = ({ context, range, message }: IRejectZeroLengthParams) => (TIME_PATTERN.test(range?.start || '') && range.start === range.end ? context.createError({ path: `${context.path}.end`, message }) : true);

interface IBreakError {
  index: number;
  field: 'start' | 'end';
  message: string;
}

// A Yup test reports a single error, so wrap one per break to flag every failing break at once, not just the first.
const rejectBreaks = (context: Yup.TestContext, breakErrors: IBreakError[]) =>
  breakErrors.length === 0 ? true : new Yup.ValidationError(breakErrors.map(({ index, field, message }) => context.createError({ path: `${context.path}.breaks[${index}].${field}`, message })));

export const buildShiftValidationSchema = (reasonMessage: string) =>
  Yup.object().shape({
    schedules: Yup.array().of(
      Yup.object()
        .shape({
          jobTitle: Yup.string().required('Please select a job title'),
          start: timeField('Clock in is required'),
          end: timeField('Clock out is required'),
          breaks: Yup.array().of(
            Yup.object()
              .shape({
                start: timeField('Break start is required'),
                end: timeField('Break end is required'),
              })
              .test('break-has-length', 'Break end matches break start', function breakHasLength(this: Yup.TestContext, laborBreak: { start: string; end: string }) {
                return rejectZeroLength({ context: this, range: laborBreak, message: "Break end can't be the same as break start" });
              })
          ),
        })
        .test('shift-has-length', 'Clock out matches clock in', function shiftHasLength(this: Yup.TestContext, schedule: { start: string; end: string }) {
          return rejectZeroLength({ context: this, range: schedule, message: "Clock out can't be the same as clock in" });
        })
        .test('breaks-within-shift', 'Break is outside the shift', function breaksWithinShift(this: Yup.TestContext, schedule: { start: string; end: string; breaks: { start: string; end: string }[] }) {
          return rejectBreaks(
            this,
            findBreaksOutsideShift(schedule).map(({ index, message }) => ({ index, field: 'end', message }))
          );
        })
        .test('breaks-do-not-overlap', 'Breaks overlap', function breaksDoNotOverlap(this: Yup.TestContext, schedule: { start: string; breaks: { start: string; end: string }[] }) {
          return rejectBreaks(
            this,
            findOverlappingBreaks(schedule).map((index) => ({ index, field: 'start', message: 'Break overlaps another break' }))
          );
        })
    ),
    reason: Yup.string().trim().required(reasonMessage).max(REASON_MAX_LENGTH, REASON_TOO_LONG_MESSAGE),
  });

const fieldError = (touched?: boolean, error?: string) => (touched && error ? error : '');

// Opens the numeric keypad on phones: Android keyboards otherwise let letters into the masked value until blur.
const timeInputProps = { inputMode: 'numeric', autoComplete: 'off', autoCorrect: 'off' } as const;

interface IShiftFieldArrayProps {
  schedules: IShiftFormSchedule[];
  jobTitles: string[];
  setFieldValue: (field: string, value: unknown) => void;
  hideSingleShiftHeader?: boolean; // Drop the "Shift 1" heading (and its remove button) while there is only one shift
}

export const ShiftFieldArray: React.FC<IShiftFieldArrayProps> = ({ schedules, jobTitles, setFieldValue, hideSingleShiftHeader = false }) => {
  const jobTitleOptions = useMemo(() => jobTitles.map(toJobTitleOption), [jobTitles]);

  const searchJobTitles = useCallback(
    ({ state }: SelectRenderer<ISelectOption<IJobTitleState>>) => {
      const query = state.search.trim();
      return query ? searchJobTitleOptions(jobTitleOptions, query) : jobTitleOptions;
    },
    [jobTitleOptions]
  );

  return (
    <FieldArray
      name="schedules"
      render={(arrayHelpers: FieldArrayRenderProps) => (
        <>
          {schedules.map((schedule, index) => (
            <EditShiftLayout key={schedule.id} data-cy={`schedule-${index}`}>
              {!(hideSingleShiftHeader && schedules.length === 1) && (
                <EditShiftHeader>
                  <ShiftHeading>Shift {index + 1}</ShiftHeading>
                  <RemoveButton label="Remove shift" icon="Trash" appearance="white" onClick={() => arrayHelpers.remove(index)} dataCy="remove-shift-button" />
                </EditShiftHeader>
              )}

              <Field name={`schedules.${index}.jobTitle`}>
                {({ field, meta }: FieldProps) => (
                  <FieldWrapper data-cy={`schedule-${index}-job-title-field`} label="Job title" message={fieldError(meta.touched, meta.error)} hasError={Boolean(fieldError(meta.touched, meta.error))}>
                    <ShiftJobTitleSelect
                      options={jobTitleOptions}
                      values={field.value ? [toJobTitleOption(field.value)] : []}
                      placeholder="Select a job title"
                      searchable
                      searchFn={searchJobTitles}
                      noDataLabel="No matching job titles"
                      valueField="label"
                      dataCy={`schedule-${index}-job-title`}
                      onChange={([option]: ISelectOption<IJobTitleState>[]) => setFieldValue(`schedules.${index}.jobTitle`, option?.value?.title || '')}
                    />
                  </FieldWrapper>
                )}
              </Field>

              <ShiftFieldsRow>
                <Field name={`schedules.${index}.start`}>
                  {({ field, meta }: FieldProps) => (
                    <TimeField data-cy={`schedule-${index}-clock-in`} label="Clock in" message={fieldError(meta.touched, meta.error)} hasError={Boolean(fieldError(meta.touched, meta.error))}>
                      <TimeMaskedInput {...timeInputProps} value={field.value} onChange={(val) => setFieldValue(`schedules.${index}.start`, val)} />
                    </TimeField>
                  )}
                </Field>
                <Field name={`schedules.${index}.end`}>
                  {({ field, meta }: FieldProps) => (
                    <TimeField data-cy={`schedule-${index}-clock-out`} label="Clock out" message={fieldError(meta.touched, meta.error)} hasError={Boolean(fieldError(meta.touched, meta.error))}>
                      <TimeMaskedInput {...timeInputProps} value={field.value} onChange={(val) => setFieldValue(`schedules.${index}.end`, val)} />
                    </TimeField>
                  )}
                </Field>
              </ShiftFieldsRow>

              <FieldArray
                name={`schedules.${index}.breaks`}
                render={(breakHelpers: FieldArrayRenderProps) => (
                  <BreaksSection>
                    {schedule.breaks.map((laborBreak, breakIndex) => (
                      <BreakRow key={laborBreak.id} data-cy={`schedule-${index}-break-${breakIndex}`}>
                        <Field name={`schedules.${index}.breaks.${breakIndex}.start`}>
                          {({ field, meta, form }: FieldProps) => {
                            // "Break is outside the shift" is reported once, under the break end. While it's showing, the
                            // break start is outlined too when it's the time (or one of the times) outside the shift.
                            const endMeta = form.getFieldMeta(`schedules.${index}.breaks.${breakIndex}.end`);
                            const isOutsideShift = Boolean(fieldError(endMeta.touched, endMeta.error)) && isTimeOutsideShift({ time: field.value, shift: schedule });
                            const error = fieldError(meta.touched, meta.error);

                            return (
                              <TimeField data-cy={`schedule-${index}-break-${breakIndex}-start`} label="Break start" message={error} hasError={Boolean(error) || isOutsideShift}>
                                <TimeMaskedInput {...timeInputProps} value={field.value} onChange={(val) => setFieldValue(`schedules.${index}.breaks.${breakIndex}.start`, val)} />
                              </TimeField>
                            );
                          }}
                        </Field>
                        <BreakEndCell>
                          <Field name={`schedules.${index}.breaks.${breakIndex}.end`}>
                            {({ field, meta }: FieldProps) => (
                              <TimeField data-cy={`schedule-${index}-break-${breakIndex}-end`} label="Break end" message={fieldError(meta.touched, meta.error)} hasError={Boolean(fieldError(meta.touched, meta.error))}>
                                <TimeMaskedInput {...timeInputProps} value={field.value} onChange={(val) => setFieldValue(`schedules.${index}.breaks.${breakIndex}.end`, val)} />
                              </TimeField>
                            )}
                          </Field>
                          <RemoveButton label="Remove break" icon="Trash" appearance="white" onClick={() => breakHelpers.remove(breakIndex)} dataCy="remove-break-button" />
                        </BreakEndCell>
                      </BreakRow>
                    ))}
                    <AddBreakButton appearance="white" size="small" label="Add break" onClick={() => breakHelpers.push({ id: uuid(), start: '', end: '' })} data-cy="add-break-button" />
                  </BreaksSection>
                )}
              />
            </EditShiftLayout>
          ))}

          <AddShiftButton appearance="white" size="small" label="Add another shift" onClick={() => arrayHelpers.push(newFormSchedule())} data-cy="add-shift-button" />
        </>
      )}
    />
  );
};
